import type {
  DataSourceDefinition,
  DataSourceDependencies,
  RuntimeEnvironment,
} from '@packages/systems/dynamo/source-types';
import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {memoize} from '@packages/utilities/memo';
import type {Gateway, GatewayRuntimeEnvironment} from './types';
import {
  buildBindingContextType,
  toTypeReference,
  EntityMapping,
  buildContextMapping,
  buildBindableProperty,
  TypeReferenceBuilder,
  PageBindingContext,
  ListBindingContext,
  EntityBindingContext,
} from './primitives';
import type {
  BindingContextType,
  RootTypeReference,
  EntityTypeReference,
  ListTypeReference,
  BindableProperty,
  BindingContextTypeReference,
} from './primitives';
import {pluginNameToString} from '@packages/systems/wfdl-Qualified';
import {WebflowEnvironments} from '@packages/systems/core-constants';

class RuntimeEnv implements RuntimeEnvironment {
  #runtimeEnvironment: GatewayRuntimeEnvironment;
  constructor(runtimeEnvironment: GatewayRuntimeEnvironment) {
    this.#runtimeEnvironment = runtimeEnvironment;
  }
  isDesigner() {
    return this.#runtimeEnvironment === WebflowEnvironments.DESIGNER;
  }
  isSSR() {
    return this.#runtimeEnvironment === WebflowEnvironments.SSR;
  }
}

function buildDependencies(
  collections: CollectionsType,
  runtimeEnvironment: GatewayRuntimeEnvironment
): DataSourceDependencies {
  return {
    getCollections() {
      return collections;
    },
    runtimeEnvironment: new RuntimeEnv(runtimeEnvironment),
  };
}

export const buildGateways = memoize(function (
  definitions: DataSourceDefinition[],
  collections: CollectionsType,
  runtimeEnvironment: GatewayRuntimeEnvironment
): Gateway[] {
  const dependencies = buildDependencies(collections, runtimeEnvironment);

  const seenPluginNames = new Set();

  return definitions.reduce<Array<Gateway>>((gateways, definition) => {
    const pluginName = pluginNameToString(definition.pluginName);
    if (seenPluginNames.has(pluginName)) {
      logGatewayError(
        `Plugin name collision '${pluginName}'. Only one data source definition can be associated with each Plugin.`
      );
      return gateways;
    }

    seenPluginNames.add(pluginName);
    gateways.push(buildGateway(definition, dependencies));
    return gateways;
  }, []);
});

function logGatewayError(message: string) {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`ERROR / buildGateway - ${message}`);
  }
}

export function buildGateway(
  plugin: DataSourceDefinition,
  dependencies: DataSourceDependencies
): Gateway {
  const typeMap = plugin.defineTypes(dependencies).reduce((tMap, typeDef) => {
    const bindingContextType = buildBindingContextType(typeDef);
    const reference = toTypeReference(bindingContextType);

    if (!tMap.get(reference)) {
      tMap.set(reference, bindingContextType);
    } else {
      logGatewayError('Duplicate type definition');
    }

    return tMap;
  }, new EntityMapping<BindingContextType>());

  const typeReferenceBuilder = new TypeReferenceBuilder(typeMap);

  const contextMap = buildContextMapping(
    plugin.defineBindingContexts<
      RootTypeReference,
      EntityTypeReference,
      ListTypeReference
    >(dependencies, typeReferenceBuilder)
  );

  const relationshipMap = plugin
    .defineRelationships<
      RootTypeReference,
      EntityTypeReference,
      ListTypeReference
    >(dependencies, typeReferenceBuilder)
    .reduce((rMap, relationshipDef) => {
      const {type, relativeContexts} = relationshipDef;

      if (!typeMap.get(type)) {
        // cannot define relative binding contexts for a missing type
        logGatewayError('Defining relationships for non-existent type');
        return rMap;
      }

      if (!rMap.get(type)) {
        const {elements} = contextMap;

        if (!elements) {
          // there is nothing to reference
          logGatewayError('No binding contexts to reference');
          return rMap;
        }

        rMap.set(
          type,
          relativeContexts.filter((contextId) => {
            if (!elements.hasOwnProperty(contextId)) {
              logGatewayError(
                `Referencing invalid binding context ${contextId}`
              );
              return false;
            }
            return true;
          })
        );
      } else {
        logGatewayError('Duplicate relationship definition');
      }

      return rMap;
    }, new EntityMapping<string[]>());

  const propsMap = plugin
    .defineBindableProperties<
      RootTypeReference,
      EntityTypeReference,
      ListTypeReference
    >(dependencies, typeReferenceBuilder)
    .reduce((pMap, bindablePropsDef) => {
      const {type, bindableProperties} = bindablePropsDef;
      if (!typeMap.get(type)) {
        // cannot define binding sources for a missing type
        logGatewayError('Defining bindable properties for non-existent type');
        return pMap;
      }

      if (!pMap.get(type)) {
        const bindingSources = bindableProperties.map((propDef) =>
          buildBindableProperty(propDef)
        );
        pMap.set(type, bindingSources);
      } else {
        logGatewayError('Duplicate bindable properties definition');
      }

      return pMap;
    }, new EntityMapping<BindableProperty[]>());

  function getRootBindingContext() {
    return contextMap.root ?? null;
  }

  function getBindingContextType(typeReference: BindingContextTypeReference) {
    return typeMap.get(typeReference);
  }

  function getRelativeBindingContexts(
    typeReference: BindingContextTypeReference
  ) {
    const {elements} = contextMap;
    const relativeBindingContexts: Array<
      EntityBindingContext | ListBindingContext
    > = [];

    if (!elements) {
      return relativeBindingContexts;
    }

    const relativeContextIds = relationshipMap.get(typeReference) || [];

    for (const contextId of relativeContextIds) {
      const relativeBindingContext = elements[contextId];
      if (relativeBindingContext) {
        relativeBindingContexts.push(relativeBindingContext);
      }
    }

    return relativeBindingContexts;
  }

  function getPageBindingContexts() {
    const {pages} = contextMap;
    const pageBindingContexts: PageBindingContext[] = [];

    if (!pages) {
      return pageBindingContexts;
    }

    for (const contextId in pages) {
      const pageBindingContext = pages[contextId];
      if (pageBindingContext) {
        pageBindingContexts.push(pageBindingContext);
      }
    }

    return pageBindingContexts;
  }

  function getBindableProperties(typeReference: BindingContextTypeReference) {
    return propsMap.get(typeReference) || [];
  }

  return {
    pluginName: plugin.pluginName,
    getRootBindingContext,
    getBindingContextType,
    getBindableProperties,
    getPageBindingContexts,
    getRelativeBindingContexts,
  };
}
